import { useCallback, useEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import { useSelector } from 'react-redux';

import type { RosterParent, SubUnitType } from '@appTypes/esb';
import { subUnitTypeSel } from '@modules/advancement/subUnits/duck/selectors';
import {
  packIdSel,
  subUnitPermissionsSel,
  subUnitsEnabledSel,
} from '@modules/roster-v2/duck/selectors';
import { collectSubUnitRefs } from '@modules/roster-v2/models/groupedRoster';
import {
  DEN_LEVEL_OPTIONS,
  type RosterMemberView,
  buildDenLevelImages,
  formatSubUnitLabel,
} from '@modules/roster-v2/models/rosterMemberView';
import type * as SubUnitAdmin from '@modules/roster-v2/models/subUnitAdmin';
import {
  assignedSubUnitUserIds,
  buildCreateArg,
  buildDeleteArg,
  buildDenChiefOptions,
  buildDenChiefRecords,
  buildMemberOptions,
  buildSubUnitBaseline,
  buildSubUnitPermissions,
  diffSubUnit,
  expiredSbUserIds,
  isNoopUpdate,
  needsOwnSubUnits,
  nextPendingLevelChange,
  takenSubUnitNames,
} from '@modules/roster-v2/models/subUnitAdmin';
import {
  useCreateSubUnitWithMembersMutation,
  useDeleteSubUnitWithMembersMutation,
  useGetUnitRosterParentsQuery,
  useGetUnitRosterYouthsQuery,
  useUpdateSubUnitWithMembersMutation,
} from '@modules/roster/api';
import type { SubUnitSaveStep } from '@modules/roster/subUnitMutations';
import {
  useGetRanksQuery,
  useGetSubUnitAdultsQuery,
  useGetSubUnitYouthsQuery,
  useGetSubUnitsQuery,
} from '@modules/rtk/esb-api';
import { errorDescOf, getErrorDesc } from '@modules/rtk/utils/esbApiUtils';
import { toastService } from '@modules/toasts';
import { intl } from '@shared/localization';

import { useMySubUnits } from './useMySubUnits';

// --- Permissions -------------------------------------------------------------

/**
 * What the user may do with the unit's dens/patrols; nothing when disabled.
 * Only `EDIT_SUB_UNIT` without `EDIT_UNIT` needs the `useMySubUnits` lookup.
 */
export function useSubUnitPermissions(
  orgGuid?: string,
): SubUnitAdmin.SubUnitPermissions {
  const subUnitType = useSelector(subUnitTypeSel);
  const subUnitsEnabled = useSelector(subUnitsEnabledSel);
  const rights = useSelector(subUnitPermissionsSel);
  const enabled = subUnitsEnabled && !!orgGuid;

  const { mySubUnits } = useMySubUnits(orgGuid ?? '', {
    skip: !enabled || !needsOwnSubUnits(rights),
  });

  return useMemo(
    () =>
      buildSubUnitPermissions({
        subUnitType: enabled ? subUnitType : undefined,
        rights,
        myAssignments: mySubUnits,
      }),
    [enabled, subUnitType, rights, mySubUnits],
  );
}

// --- Sources -----------------------------------------------------------------

const EMPTY_PARENTS: RosterParent[] = [];

interface QueryStatus {
  isSuccess: boolean;
  isUninitialized: boolean;
  isError: boolean;
  error?: unknown;
}

/** Answered with data, or skipped (so a closing modal doesn't change). */
const isSettled = (query: QueryStatus) =>
  query.isSuccess || query.isUninitialized;

/** Error of the last attempt; cleared while it retries. */
const errorOf = (query: QueryStatus) =>
  query.isError ? query.error : undefined;

interface UseSubUnitSourcesInput {
  orgGuid: string;
  subUnitType: SubUnitType;
  members: readonly RosterMemberView[];
  /** Fetch only while a sub-unit modal is open */
  active: boolean;
  /** Also load the unit roster parents, for the youth payloads */
  withParents?: boolean;
}

/**
 * Server data the sub-unit modals need beyond the roster. `refetching` covers
 * every baseline source (refetched after each save). A failed request is never
 * settled: its `*Error` is set until `retry`.
 */
function useSubUnitSources(input: UseSubUnitSourcesInput) {
  const { orgGuid, subUnitType, members, active, withParents = false } = input;
  const skip = !active || !orgGuid;
  const withDenChiefs = subUnitType === 'den';

  const subUnitsQuery = useGetSubUnitsQuery({ orgGuid }, { skip });
  // Same cache entries as the Sub-Unit column; read here for their fetch state
  const youthsQuery = useGetSubUnitYouthsQuery({ orgGuid }, { skip });
  const adultsQuery = useGetSubUnitAdultsQuery({ orgGuid }, { skip });
  const unitYouthsQuery = useGetUnitRosterYouthsQuery(
    { organizationGuid: orgGuid },
    { skip: skip || !withDenChiefs },
  );
  const parentsQuery = useGetUnitRosterParentsQuery(
    { organizationGuid: orgGuid },
    { skip: skip || !withParents },
  );

  const unitYouths = unitYouthsQuery.data?.users;
  const denChiefRecords = useMemo(
    () => buildDenChiefRecords(unitYouths),
    [unitYouths],
  );
  // Undefined (no Den Chiefs tab) for patrols or until the records load
  const denChiefs: SubUnitAdmin.DenChiefsByUserId | undefined =
    withDenChiefs && unitYouths ? denChiefRecords : undefined;

  const subUnits = subUnitsQuery.data;
  const labels = useMemo(
    () =>
      new Map(
        collectSubUnitRefs(subUnitType, members, subUnits).map(ref => [
          ref.id,
          ref.label,
        ]),
      ),
    [subUnits, members, subUnitType],
  );
  const labelOf = useCallback((id: number) => labels.get(id), [labels]);

  const assignedYouths = youthsQuery.data?.members;
  const assignedAdults = adultsQuery.data?.members;
  const assignedUserIds = useCallback(
    (id: number) =>
      assignedSubUnitUserIds(
        [...(assignedYouths ?? []), ...(assignedAdults ?? [])],
        subUnitType,
        id,
      ),
    [assignedYouths, assignedAdults, subUnitType],
  );

  const queries = [
    subUnitsQuery,
    youthsQuery,
    adultsQuery,
    unitYouthsQuery,
    parentsQuery,
  ];
  const retry = () =>
    queries.filter(query => query.isError).forEach(query => query.refetch());

  return {
    subUnits,
    subUnitsSettled: isSettled(subUnitsQuery),
    subUnitsError: errorOf(subUnitsQuery),
    denChiefs,
    denChiefsSettled: isSettled(unitYouthsQuery),
    denChiefsError: errorOf(unitYouthsQuery),
    parents: parentsQuery.data ?? EMPTY_PARENTS,
    parentsSettled: isSettled(parentsQuery),
    parentsError: errorOf(parentsQuery),
    /** `subUnitYouths` and `subUnitAdults` */
    membersSettled: isSettled(youthsQuery) && isSettled(adultsQuery),
    membersError: errorOf(youthsQuery) ?? errorOf(adultsQuery),
    retry,
    refetching:
      subUnitsQuery.isFetching ||
      youthsQuery.isFetching ||
      adultsQuery.isFetching ||
      (withDenChiefs && unitYouthsQuery.isFetching),
    labelOf,
    /** Everyone assigned to a sub-unit, including people off the roster */
    assignedUserIds,
  };
}

function useSubUnitOptions(input: {
  members: readonly RosterMemberView[];
  subUnitType: SubUnitType;
  editingSubUnitId?: number;
  /** Leave out to hide the Den Chiefs tab */
  denChiefs?: SubUnitAdmin.DenChiefsByUserId;
  labelOf: (subUnitId: number) => string | undefined;
}) {
  const { members, subUnitType, editingSubUnitId, denChiefs, labelOf } = input;

  const memberOptions = useMemo(
    () =>
      buildMemberOptions({ members, subUnitType, editingSubUnitId, denChiefs }),
    [members, subUnitType, editingSubUnitId, denChiefs],
  );
  const denChiefOptions = useMemo(
    () =>
      subUnitType === 'den' && denChiefs
        ? buildDenChiefOptions({ denChiefs, editingSubUnitId, labelOf })
        : undefined,
    [subUnitType, denChiefs, editingSubUnitId, labelOf],
  );

  return { ...memberOptions, denChiefOptions };
}

// --- Add / edit --------------------------------------------------------------

const NO_FAILURES: SubUnitSaveStep[] = [];

function emptyValues(subUnitType: SubUnitType): SubUnitAdmin.SubUnitValues {
  return {
    name: '',
    denType: subUnitType === 'den' ? DEN_LEVEL_OPTIONS[0]?.value : undefined,
    youthIds: [],
    adultIds: [],
    denChiefIds: [],
  };
}

/**
 * A partly failed save resolves, so the RTK Sentry logger never sees it. The
 * event's fetch breadcrumbs show the calls that failed.
 */
function reportPartialSave(extra: {
  action: 'create' | 'update';
  orgGuid: string;
  subUnitType: SubUnitType;
  subUnitId: number;
  failed: SubUnitSaveStep[];
}) {
  Sentry.captureMessage('Failed Sub-Unit Save', {
    level: 'error',
    tags: { subUnitType: extra.subUnitType },
    extra,
  });
}

/**
 * Dens whose level change saved but whose youths weren't re-posted. The server
 * doesn't show youth positions, so the page keeps this across modal openings.
 */
export interface PendingLevelChanges {
  has: (subUnitId: number) => boolean;
  set: (subUnitId: number, pending: boolean) => void;
}

const NO_PENDING_LEVEL_CHANGES: ReadonlySet<number> = new Set();

export function usePendingLevelChanges(): PendingLevelChanges {
  const [ids, setIds] = useState(NO_PENDING_LEVEL_CHANGES);
  const set = useCallback((subUnitId: number, pending: boolean) => {
    setIds(current => {
      if (current.has(subUnitId) === pending) return current;
      const next = new Set(current);
      if (pending) next.add(subUnitId);
      else next.delete(subUnitId);
      return next;
    });
  }, []);
  return useMemo<PendingLevelChanges>(
    () => ({ has: id => ids.has(id), set }),
    [ids, set],
  );
}

interface UseSubUnitEditorInput {
  open: boolean;
  orgGuid: string;
  subUnitType: SubUnitType;
  members: readonly RosterMemberView[];
  /** Leave out to add a sub-unit */
  initialSubUnitId?: number;
  pendingLevelChanges: PendingLevelChanges;
  onClose: () => void;
}

/**
 * Add/edit sub-unit modal state. The baseline is rebuilt from refetched server
 * data, so a resubmit sends only what's missing, and a partly failed create
 * switches to Edit. `initialValues` is `undefined` until the form can open.
 */
export function useSubUnitEditor(input: UseSubUnitEditorInput) {
  const {
    open,
    orgGuid,
    subUnitType,
    members,
    initialSubUnitId,
    pendingLevelChanges,
    onClose,
  } = input;
  const isDen = subUnitType === 'den';

  const [subUnitId, setSubUnitId] = useState(initialSubUnitId);
  const [partialFailure, setPartialFailure] = useState(NO_FAILURES);
  const [initialValues, setInitialValues] =
    useState<SubUnitAdmin.SubUnitValues>();
  const mode: SubUnitAdmin.SubUnitFormMode =
    subUnitId === undefined ? 'add' : 'edit';

  const sources = useSubUnitSources({
    orgGuid,
    subUnitType,
    members,
    active: open,
    withParents: true,
  });
  const { denChiefs } = sources;
  const { data: ranks } = useGetRanksQuery(undefined, {
    skip: !open || !isDen,
  });
  const packId = useSelector(packIdSel);

  const [createSubUnit, createState] = useCreateSubUnitWithMembersMutation();
  const [updateSubUnit, updateState] = useUpdateSubUnitWithMembersMutation();
  const saving = createState.isLoading || updateState.isLoading;

  const subUnit =
    subUnitId === undefined
      ? undefined
      : sources.subUnits?.find(item => item.subUnitId === subUnitId);
  const baseline = useMemo(
    () =>
      subUnit &&
      buildSubUnitBaseline({ subUnitType, subUnit, members, denChiefs }),
    [subUnit, subUnitType, members, denChiefs],
  );

  const options = useSubUnitOptions({
    members,
    subUnitType,
    editingSubUnitId: subUnitId,
    denChiefs,
    labelOf: sources.labelOf,
  });
  const takenNames = useMemo(
    () => takenSubUnitNames(sources.subUnits ?? [], subUnitId),
    [sources.subUnits, subUnitId],
  );
  const denLevelImages = useMemo(() => buildDenLevelImages(ranks), [ranks]);

  const loadFailed =
    sources.membersError !== undefined ||
    sources.denChiefsError !== undefined ||
    sources.parentsError !== undefined ||
    sources.subUnitsError !== undefined;

  // Set during render so no empty frame shows; fixed for the session
  const canOpen =
    !loadFailed &&
    sources.membersSettled &&
    sources.denChiefsSettled &&
    (mode === 'add' || (!!baseline && !sources.refetching));
  if (open && canOpen && !initialValues) {
    setInitialValues(baseline ?? emptyValues(subUnitType));
  }

  // The edited sub-unit was deleted meanwhile
  const missing =
    open &&
    !initialValues &&
    mode === 'edit' &&
    sources.subUnitsSettled &&
    !sources.refetching &&
    !subUnit;
  useEffect(() => {
    if (!missing) return;
    toastService.error(intl.formatMessage({ id: 'shared.errors.generic' }));
    onClose();
  }, [missing, onClose]);

  const saveDisabled =
    sources.refetching ||
    loadFailed ||
    !sources.subUnitsSettled ||
    !sources.parentsSettled ||
    (mode === 'edit' && !baseline);

  const expiredUserIds = useMemo(() => expiredSbUserIds(members), [members]);
  const context: SubUnitAdmin.SubUnitPayloadContext = {
    parents: sources.parents,
    denChiefs,
    packId,
    expiredUserIds,
  };

  const succeed = (messageId: string, values: SubUnitAdmin.SubUnitValues) => {
    const name = formatSubUnitLabel(
      { subUnitName: values.name, denType: values.denType },
      subUnitType,
    );
    toastService.success(intl.formatMessage({ id: messageId }, { name }));
    onClose();
  };

  const create = async (values: SubUnitAdmin.SubUnitValues) => {
    const result = await createSubUnit(
      buildCreateArg({ orgGuid, subUnitType, values, context }),
    );
    if ('error' in result) {
      toastService.error(getErrorDesc(result.error));
      return;
    }

    const { subUnitId: createdId, failed } = result.data;
    if (failed.length === 0) {
      succeed(`rosterV2.subUnits.toast.created.${subUnitType}`, values);
      return;
    }
    reportPartialSave({
      action: 'create',
      orgGuid,
      subUnitType,
      subUnitId: createdId,
      failed,
    });
    // Some people didn't get in: edit the new sub-unit to send the rest
    setSubUnitId(createdId);
    setPartialFailure(failed);
  };

  const update = async (
    editingId: number,
    values: SubUnitAdmin.SubUnitValues,
  ) => {
    if (!baseline) return;
    const levelInput = {
      subUnitType,
      values,
      baseline,
      pendingLevelChange: pendingLevelChanges.has(editingId),
    };
    const arg = diffSubUnit({
      ...levelInput,
      orgGuid,
      subUnitId: editingId,
      context,
    });
    // Nothing to send: close without a toast, and nothing stays pending
    if (isNoopUpdate(arg)) {
      pendingLevelChanges.set(editingId, false);
      onClose();
      return;
    }

    const result = await updateSubUnit(arg);
    // Not expected: the update reports failed steps instead of erroring
    if ('error' in result) {
      toastService.error(getErrorDesc(result.error));
      return;
    }

    const { failed } = result.data;
    pendingLevelChanges.set(
      editingId,
      nextPendingLevelChange({ ...levelInput, failed }),
    );
    if (failed.length === 0) {
      succeed(`rosterV2.subUnits.toast.saved.${subUnitType}`, values);
      return;
    }
    reportPartialSave({
      action: 'update',
      orgGuid,
      subUnitType,
      subUnitId: editingId,
      failed,
    });
    // Stay in Edit: the refetched baseline shows what is still missing
    setPartialFailure(failed);
  };

  const handleSubmit = (values: SubUnitAdmin.SubUnitValues) => {
    // RTK Query doesn't merge duplicate mutation calls
    if (saving || saveDisabled) return;
    if (subUnitId === undefined) create(values);
    else update(subUnitId, values);
  };

  return {
    /** The initial sub-unit, or the one just created */
    subUnitId,
    /** `SubUnitModalView` props, minus `open` and the page's callbacks */
    viewProps: {
      mode,
      subUnitType,
      initialValues,
      youthOptions: options.youthOptions,
      adultOptions: options.adultOptions,
      denChiefOptions: options.denChiefOptions,
      denLevelImages,
      takenNames,
      saving,
      saveDisabled,
      partialFailure,
      loadFailed,
      onRetryLoad: sources.retry,
      onSubmit: handleSubmit,
    },
  };
}

// --- Delete ------------------------------------------------------------------

interface UseDeleteSubUnitInput {
  open: boolean;
  orgGuid: string;
  subUnitType: SubUnitType;
  subUnitId: number;
  members: readonly RosterMemberView[];
  onDeleted: () => void;
}

/**
 * Delete confirm: empties the sub-unit, then deletes it. The arg is rebuilt
 * from refetched server data, so a retry sends only what's left. A failed
 * load sets `loadFailed` and never reads as an empty sub-unit.
 */
export function useDeleteSubUnit(input: UseDeleteSubUnitInput) {
  const { open, orgGuid, subUnitType, subUnitId, members, onDeleted } = input;

  const [failed, setFailed] = useState(false);
  // Frozen once shown: the label leaves the roster as members are removed
  const [subUnitName, setSubUnitName] = useState<string>();

  const sources = useSubUnitSources({
    orgGuid,
    subUnitType,
    members,
    active: open,
  });
  const { denChiefs, assignedUserIds } = sources;
  const [deleteSubUnit, { isLoading: deleting }] =
    useDeleteSubUnitWithMembersMutation();

  const membersKnown = sources.membersSettled && sources.denChiefsSettled;
  const arg = useMemo(() => {
    if (!membersKnown) return undefined;
    // Only the member lists matter here
    const subUnit = sources.subUnits?.find(
      item => item.subUnitId === subUnitId,
    ) ?? { subUnitId, subUnitName: '', denType: null };
    const baseline = buildSubUnitBaseline({
      subUnitType,
      subUnit,
      members,
      denChiefs,
    });
    return buildDeleteArg({
      orgGuid,
      subUnitType,
      subUnitId,
      baseline,
      denChiefs,
      // Off-roster people too: only an empty sub-unit can be deleted
      assignedUserIds: assignedUserIds(subUnitId),
    });
  }, [
    membersKnown,
    sources.subUnits,
    subUnitId,
    subUnitType,
    members,
    denChiefs,
    orgGuid,
    assignedUserIds,
  ]);

  const label = sources.labelOf(subUnitId);
  const nameKnown = subUnitName !== undefined || label !== undefined;
  // `getSubUnits` only matters for the name, when no roster member has it
  const loadFailed =
    sources.membersError !== undefined ||
    sources.denChiefsError !== undefined ||
    (!nameKnown && sources.subUnitsError !== undefined);
  const canOpen = !!arg && (label !== undefined || sources.subUnitsSettled);
  if (open && canOpen && subUnitName === undefined) {
    setSubUnitName(label ?? '');
  }

  // Retry waits for the refetch, so it is built from the fresh baseline
  const busy = deleting || sources.refetching;

  const handleConfirm = async () => {
    if (busy || loadFailed || !arg) return;
    const result = await deleteSubUnit(arg);
    if ('error' in result) {
      setFailed(true);
      // The confirm shows the failure; toast the server's reason, if any
      const desc = errorDescOf(result.error);
      if (desc) toastService.error(desc);
      return;
    }
    toastService.success(
      intl.formatMessage(
        { id: `rosterV2.subUnits.toast.deleted.${subUnitType}` },
        { name: subUnitName },
      ),
    );
    onDeleted();
  };

  return {
    /** `DeleteSubUnitModalView` props, minus `open` and `onCancel` */
    viewProps: {
      subUnitType,
      subUnitName: subUnitName ?? label,
      memberCount:
        subUnitName !== undefined && arg
          ? arg.memberUserIds.length + arg.denChiefs.length
          : undefined,
      deleting: busy,
      failed,
      loadFailed,
      onRetryLoad: sources.retry,
      onConfirm: handleConfirm,
    },
  };
}
